import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import CoachVoiceButton from "../../components/AICoach/CoachVoiceButton";
import DigipingMark from "../../digiping/DigipingMark";
import { sendMessage } from "../../components/funtionsBD/aiCoachService";
import { currentAppLanguage } from "../../utils/appLanguages";
import { detectInputLanguage } from "../../utils/detectInputLanguage";
import { toCoachLanguage } from "../../voice";
import { getLiveTrainingPortalRoot } from "../../hooks/useLiveFullscreen";
import {
  parseTableSetupVoiceCommand,
  presentPlayerVoiceHints,
  groupRosterNotPresentHints,
  playerVoiceLabel,
} from "../../utils/tableSetupVoiceCommands";
import "./LiveTrainingTableSetupPingfloVoice.css";

/**
 * While validating supported table voice commands, coach reviews the transcript
 * and taps Send. Set to false later to auto-send when speech ends.
 */
export const TABLE_SETUP_VOICE_REQUIRE_CONFIRM = true;

function findSeatForPlayer(tables, playerId) {
  const pid = String(playerId || "");
  if (!pid) return null;
  for (const table of tables || []) {
    for (const slot of ["player1", "player2", "player3"]) {
      const p = table?.[slot];
      const id = p?.playerId || p?.player_id || null;
      if (id != null && String(id) === pid) {
        return { table, slot };
      }
    }
    const extras = Array.isArray(table?.extras) ? table.extras : [];
    for (let i = 0; i < extras.length; i += 1) {
      const p = extras[i];
      const id = p?.playerId || p?.player_id || null;
      if (id != null && String(id) === pid) {
        return { table, slot: `extra:${i}` };
      }
    }
  }
  return null;
}

function LevelMeter({ level = 0, active = false }) {
  const bars = 7;
  const normalized = Math.max(0, Math.min(1, Number(level) || 0));
  return (
    <div
      className={`lt-pingflo-voice__meter${active ? " is-active" : ""}`}
      aria-hidden
    >
      {Array.from({ length: bars }, (_, i) => {
        const threshold = (i + 1) / (bars + 1);
        const lit = active && normalized >= threshold * 0.55;
        const height = 28 + ((i % 3) + 1) * 10;
        return (
          <span
            key={i}
            className={`lt-pingflo-voice__bar${lit ? " is-lit" : ""}`}
            style={{
              height: `${height}%`,
              opacity: lit ? 0.55 + normalized * 0.45 : 0.22,
              transform: lit
                ? `scaleY(${0.55 + normalized * 0.7})`
                : "scaleY(0.35)",
            }}
          />
        );
      })}
    </div>
  );
}

/**
 * Pingflo voice for coach tables:
 * - micro: icon button in the header (default for table config)
 * - card: legacy labeled card (wizard / fallback)
 *
 * Prefer local phonetic parse against present session players, then AI fallback.
 */
export function LiveTrainingTableSetupPingfloVoice({
  disabled = false,
  tsId = null,
  teId = null,
  grId = null,
  primaryColor = "#0877BC",
  onSeatingChanged,
  className = "",
  variant = "micro",
  presentPlayers = [],
  groupRoster = [],
  coachTables = [],
  onVoiceCommand = null,
  selectedPlayers = [],
  onContextMessage = null,
}) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [livePartial, setLivePartial] = useState("");
  const [voiceLanguage, setVoiceLanguage] = useState(null);
  const [sending, setSending] = useState(false);
  const [reply, setReply] = useState(null);
  const [error, setError] = useState(null);
  const [listening, setListening] = useState(false);
  const [level, setLevel] = useState(0);
  const [editing, setEditing] = useState(false);
  /** True only after final STT (or coach edit) — keeps Send off during live partials. */
  const [transcriptReady, setTranscriptReady] = useState(false);
  const [autoStartToken, setAutoStartToken] = useState(0);
  const conversationIdRef = useRef(null);
  const textareaRef = useRef(null);
  const rootRef = useRef(null);
  const editingRef = useRef(false);
  /** True only after the coach types. Focusing the field must not drop the final transcript. */
  const userEditedRef = useRef(false);
  /** idle | live | final — final blocks a late listening(true) from wiping the transcript. */
  const voicePhaseRef = useRef("idle");
  /** After Send, the next spoken words start a fresh command (drop prior text). */
  const freshCommandRef = useRef(false);
  /** Players waiting for a spoken table number after need_table. */
  const pendingMovePlayersRef = useRef([]);

  useEffect(() => {
    editingRef.current = editing;
  }, [editing]);

  useEffect(() => {
    const dialog = document.querySelector(".lt-coach-tables-dialog");
    if (!dialog) return undefined;
    dialog.classList.toggle("lt-pingflo-caption-open", open);
    window.dispatchEvent(
      new CustomEvent("smarttt:pingflo-caption", { detail: { open } })
    );
    return () => {
      dialog.classList.remove("lt-pingflo-caption-open");
      window.dispatchEvent(
        new CustomEvent("smarttt:pingflo-caption", { detail: { open: false } })
      );
    };
  }, [open]);

  useEffect(() => {
    if (!open || !editing) return;
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    const len = el.value.length;
    try {
      el.setSelectionRange(len, len);
    } catch {
      /* ignore */
    }
  }, [open, editing]);

  const reportContextMessage = useCallback(
    (message) => {
      if (typeof onContextMessage === "function") {
        onContextMessage(message || null);
      }
    },
    [onContextMessage]
  );

  const clearCaptionText = useCallback(() => {
    setDraft("");
    setLivePartial("");
    setVoiceLanguage(null);
    setEditing(false);
    setLevel(0);
    setTranscriptReady(false);
  }, []);

  const resetDraft = useCallback(() => {
    pendingMovePlayersRef.current = [];
    clearCaptionText();
    setListening(false);
    setOpen(false);
    setError(null);
  }, [clearCaptionText]);

  const openListener = useCallback(() => {
    if (disabled || sending || !tsId || !teId) return;
    setError(null);
    setReply(null);
    setLivePartial("");
    setEditing(false);
    setOpen(true);
    setAutoStartToken((n) => n + 1);
  }, [disabled, sending, teId, tsId]);

  const selectedPlayerNames = useMemo(
    () =>
      (selectedPlayers || [])
        .map((p) => String(p?.name || "").trim())
        .filter(Boolean),
    [selectedPlayers]
  );

  const executeLocalCommand = useCallback(
    async (cmd) => {
      if (!cmd || typeof onVoiceCommand !== "function") return null;
      if (cmd.incomplete) {
        if (cmd.reason === "need_two_players") {
          return {
            ok: false,
            message:
              t("liveTrainingTableVoiceNeedTwoPlayers") ||
              "Say two present players to switch.",
          };
        }
        if (cmd.reason === "need_table") {
          pendingMovePlayersRef.current = Array.isArray(cmd.players)
            ? cmd.players
            : [];
          return {
            ok: false,
            message:
              t("liveTrainingTableVoiceNeedTable") ||
              "Say a table number, e.g. table 2 or two.",
          };
        }
        if (cmd.reason === "need_player") {
          return {
            ok: false,
            message:
              t("liveTrainingTableVoiceNeedPresentPlayer") ||
              "Say a present player.",
          };
        }
        if (cmd.reason === "need_group_player") {
          return {
            ok: false,
            message:
              t("liveTrainingTableVoiceNeedGroupPlayer") ||
              "Say a group player to add.",
          };
        }
        if (cmd.reason === "ambiguous_group_player") {
          const names = (cmd.players || []).map(playerVoiceLabel).join(", ");
          return {
            ok: false,
            message:
              t("liveTrainingTableVoiceAmbiguousGroupPlayer", { names }) ||
              `Several players in this group match. Say the last name: ${names}.`,
          };
        }
        if (cmd.reason === "already_present") {
          const names = (cmd.players || []).map(playerVoiceLabel).join(", ");
          return {
            ok: false,
            message:
              t("liveTrainingTableVoiceAlreadyPresent", { name: names }) ||
              `${names} already present.`,
          };
        }
        return { ok: false, message: t("error") || "Could not understand." };
      }

      if (cmd.action === "rotate_left") {
        const ok = await onVoiceCommand({ type: "rotate", direction: "left" });
        return {
          ok: Boolean(ok),
          message: t("liveTrainingTableVoiceRotatedLeft") || "Rotated left.",
        };
      }
      if (cmd.action === "rotate_right") {
        const ok = await onVoiceCommand({ type: "rotate", direction: "right" });
        return {
          ok: Boolean(ok),
          message: t("liveTrainingTableVoiceRotatedRight") || "Rotated right.",
        };
      }
      if (cmd.action === "sort_ranking_desc") {
        const ok = await onVoiceCommand({
          type: "sort_ranking",
          ascending: false,
        });
        return {
          ok: Boolean(ok),
          message: t("liveTrainingTableVoiceSortedDesc") || "Sorted by ranking.",
        };
      }
      if (cmd.action === "sort_ranking_asc") {
        const ok = await onVoiceCommand({
          type: "sort_ranking",
          ascending: true,
        });
        return {
          ok: Boolean(ok),
          message:
            t("liveTrainingTableVoiceSortedAsc") || "Sorted low → high.",
        };
      }
      if (cmd.action === "add_player") {
        const players = cmd.players || [];
        const added = [];
        for (const player of players) {
          const pid = player?.player_id || player?.playerId || player?.id;
          const ok = await onVoiceCommand({
            type: "mark_present",
            playerId: pid,
            player,
          });
          if (ok) added.push(playerVoiceLabel(player));
        }
        return {
          ok: added.length > 0,
          message:
            t("liveTrainingTableVoiceAdded", { name: added.join(", ") }) ||
            `${added.join(", ")} added.`,
        };
      }
      if (cmd.action === "switch_players") {
        const [a, b] = cmd.players || [];
        const seatA = findSeatForPlayer(
          coachTables,
          a?.player_id || a?.playerId || a?.id
        );
        const seatB = findSeatForPlayer(
          coachTables,
          b?.player_id || b?.playerId || b?.id
        );
        if (!seatA || !seatB) {
          return {
            ok: false,
            message:
              t("liveTrainingTableVoicePlayersMustBeSeated") ||
              "Both must be seated.",
          };
        }
        const ok = await onVoiceCommand({
          type: "switch",
          fromTable: seatA.table,
          fromSlot: seatA.slot,
          toTable: seatB.table,
          toSlot: seatB.slot,
        });
        return {
          ok: Boolean(ok),
          message:
            t("liveTrainingTableVoiceSwitchDone") ||
            `${playerVoiceLabel(a)} ↔ ${playerVoiceLabel(b)}.`,
        };
      }
      if (cmd.action === "move_player") {
        const movers = cmd.players || [];
        const moved = [];
        for (const player of movers) {
          const pid = player?.player_id || player?.playerId || player?.id;
          const ok = await onVoiceCommand({
            type: "move",
            playerId: pid,
            tableNumber: cmd.tableNumber,
            force: true,
          });
          if (ok) moved.push(playerVoiceLabel(player));
        }
        if (moved.length > 0) {
          pendingMovePlayersRef.current = [];
        }
        return {
          ok: moved.length > 0,
          message:
            t("liveTrainingTableVoiceMoveDone", {
              name: moved.join(", "),
              table: cmd.tableNumber,
            }) || `${moved.join(", ")} → T${cmd.tableNumber}`,
        };
      }
      return null;
    },
    [coachTables, onVoiceCommand, t]
  );

  const sendCommand = useCallback(
    async (rawText, languageHint = null) => {
      const text = (rawText || "").trim();
      if (!text || sending || disabled) return;

      setSending(true);
      setError(null);
      setReply(null);

      try {
        const local = parseTableSetupVoiceCommand(text, {
          presentPlayers,
          groupRoster:
            Array.isArray(groupRoster) && groupRoster.length
              ? groupRoster
              : presentPlayers,
          language: languageHint || currentAppLanguage(i18n),
          pendingMovePlayers: pendingMovePlayersRef.current,
        });
        if (local && !local.deferToAi && onVoiceCommand) {
          const result = await executeLocalCommand(local);
          if (result) {
            if (result.ok) {
              pendingMovePlayersRef.current = [];
              clearCaptionText();
              freshCommandRef.current = true;
              if (typeof onSeatingChanged === "function") {
                await onSeatingChanged();
              }
              setReply(result.message);
              reportContextMessage(result.message);
              setOpen(false);
            } else {
              setError(result.message);
              reportContextMessage(result.message);
            }
            return;
          }
        }

        const detectedLang = detectInputLanguage(text);
        const presentNames = presentPlayerVoiceHints(presentPlayers);
        const notPresentNames = groupRosterNotPresentHints(
          Array.isArray(groupRoster) && groupRoster.length
            ? groupRoster
            : presentPlayers
        );
        let aiText = text;
        if (
          local?.action === "create_station" &&
          selectedPlayerNames.length > 0 &&
          !(local.players || []).length
        ) {
          aiText = `${text} — players: ${selectedPlayerNames.join(", ")}`;
        }
        const context = {
          ts_id: tsId || null,
          te_id: teId || null,
          gr_id: grId || null,
          live_training: true,
          table_setup: true,
          home_role: "coach",
          present_player_names: presentNames,
          present_only: true,
          concise_replies: true,
        };
        if (notPresentNames.length) {
          context.group_roster_not_present_names = notPresentNames;
        }
        if (selectedPlayerNames.length) {
          context.selected_player_names = selectedPlayerNames;
        }
        if (detectedLang) {
          context.language = detectedLang;
        } else if (languageHint) {
          context.language = toCoachLanguage(languageHint);
        } else {
          context.language = currentAppLanguage(i18n);
        }

        const result = await sendMessage(
          aiText,
          conversationIdRef.current,
          context,
          { input_mode: "voice" }
        );
        if (result?.conversationId) {
          conversationIdRef.current = result.conversationId;
        }
        if (typeof onSeatingChanged === "function") {
          await onSeatingChanged();
        }
        const botText = (result?.message || "").trim();
        clearCaptionText();
        freshCommandRef.current = true;
        const okMsg =
          botText ||
          t("liveTrainingTableWizardVoiceApplied") ||
          "Done.";
        setReply(okMsg);
        reportContextMessage(okMsg);
        setOpen(false);
      } catch (err) {
        const errMsg =
          err?.message ||
          t("liveTrainingTableWizardVoiceError") ||
          "Voice command failed.";
        setError(errMsg);
        reportContextMessage(errMsg);
      } finally {
        setSending(false);
      }
    },
    [
      clearCaptionText,
      disabled,
      executeLocalCommand,
      grId,
      groupRoster,
      i18n,
      onSeatingChanged,
      onVoiceCommand,
      presentPlayers,
      reportContextMessage,
      selectedPlayerNames,
      sending,
      t,
      teId,
      tsId,
    ]
  );

  const handlePartial = useCallback((text) => {
    const cleaned = (text || "").trim();
    if (freshCommandRef.current && cleaned) {
      freshCommandRef.current = false;
      setDraft(cleaned);
      setLivePartial(cleaned);
      setReply(null);
      setError(null);
      setEditing(false);
      return;
    }
    setLivePartial(cleaned);
    if (!editingRef.current && cleaned) {
      setDraft(cleaned);
    }
  }, []);

  const handleTranscript = useCallback(
    ({ text, language }) => {
      const cleaned = (text || "").trim();
      if (!cleaned) return;
      if (freshCommandRef.current) {
        freshCommandRef.current = false;
      }
      setError(null);
      setReply(null);
      setVoiceLanguage(language || null);
      setLivePartial("");
      if (voicePhaseRef.current === "live") voicePhaseRef.current = "final";
      if (TABLE_SETUP_VOICE_REQUIRE_CONFIRM) {
        if (!userEditedRef.current) {
          setDraft(cleaned);
        }
        setTranscriptReady(true);
        setOpen(true);
        return;
      }
      setTranscriptReady(true);
      void sendCommand(cleaned, language);
    },
    [sendCommand]
  );

  const handleListeningChange = useCallback((isListening) => {
    const on = Boolean(isListening);
    if (on) {
      if (voicePhaseRef.current === "live" || voicePhaseRef.current === "final") return;
      voicePhaseRef.current = "live";
      setListening(true);
      setTranscriptReady(false);
      // Fresh STT buffer for this take — prior validated draft is replaced.
      freshCommandRef.current = true;
      userEditedRef.current = false;
      setLivePartial("");
      if (!editingRef.current) {
        setDraft("");
        setReply(null);
        setError(null);
      }
      return;
    }
    // Stay not-ready until onFinal validates the transcript (PROCESSING gap).
    voicePhaseRef.current = "idle";
    setListening(false);
    setLevel(0);
  }, []);

  const handleSendClick = () => {
    if (!transcriptReady || listening) return;
    const visible = String(textareaRef.current?.value || "").trim();
    const text = visible || String(draft || "").trim();
    void sendCommand(text, voiceLanguage);
  };

  const languageHint = currentAppLanguage(i18n) || "en";
  const displayLive = listening && !editing ? livePartial || draft : draft;
  const canSend =
    transcriptReady &&
    Boolean(String(draft || "").trim()) &&
    !listening &&
    !sending &&
    !disabled;
  const micDisabled = disabled || sending || !tsId || !teId;
  const statusLabel = listening
    ? t("liveTrainingTableVoiceListening") || "Listening…"
    : editing
      ? t("liveTrainingTableVoiceEditing") || "Editing command"
      : transcriptReady && draft.trim()
        ? t("liveTrainingTableVoiceReady") || "Ready to send"
        : t("liveTrainingTableVoiceIdle") || "Tap mic and speak";

  const captionSlot =
    typeof document !== "undefined"
      ? document.querySelector(".lt-coach-tables-dialog__caption-slot")
      : null;
  const dialogRoot =
    typeof document !== "undefined"
      ? document.querySelector(".lt-coach-tables-dialog")
      : null;
  const portalRoot =
    captionSlot || dialogRoot || getLiveTrainingPortalRoot() || document.body;
  const portalInCaption = Boolean(captionSlot);

  const listenerCaption =
    TABLE_SETUP_VOICE_REQUIRE_CONFIRM && open
      ? createPortal(
          <div
            className={`lt-pingflo-voice__portal${
              portalInCaption
                ? " lt-pingflo-voice__portal--caption"
                : dialogRoot
                  ? ""
                  : " lt-pingflo-voice__portal--viewport"
            }`}
            role="presentation"
          >
            <div
              className={`lt-pingflo-voice__caption${
                listening ? " is-listening" : ""
              }${editing ? " is-editing" : ""}`}
              role="region"
              aria-label={
                t("liveTrainingTableWizardVoiceLabel") || "Pingflo"
              }
              style={{ "--lt-brand": primaryColor }}
            >
              <span className="lt-pingflo-voice__mark" aria-hidden>
                <DigipingMark
                  agentId="pingflo"
                  size={22}
                  primary={primaryColor}
                  showGlow={listening}
                  title={t("liveTrainingTableWizardVoiceLabel") || "Pingflo"}
                />
              </span>

              <div className="lt-pingflo-voice__field-wrap">
                <p
                  className={`lt-pingflo-voice__status${
                    listening ? " is-live" : ""
                  }`}
                  aria-live="polite"
                >
                  {listening ? <GraphicEqIcon fontSize="inherit" /> : null}
                  {statusLabel}
                </p>
                <textarea
                  id="lt-pingflo-voice-input"
                  ref={textareaRef}
                  className="lt-pingflo-voice__input"
                  rows={editing ? 2 : 1}
                  value={displayLive}
                  disabled={sending}
                  readOnly={listening && !editing}
                  onFocus={() => {
                    setEditing(true);
                    setReply(null);
                    setError(null);
                  }}
                  onClick={() => {
                    setEditing(true);
                    setReply(null);
                    setError(null);
                  }}
                  onBlur={() => {
                    if (!(draft || "").trim()) setEditing(false);
                  }}
                  onChange={(e) => {
                    userEditedRef.current = true;
                    setEditing(true);
                    setDraft(e.target.value);
                    setLivePartial("");
                    setTranscriptReady(Boolean(e.target.value.trim()));
                    setReply(null);
                    setError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      if (canSend) handleSendClick();
                    }
                    if (e.key === "Escape" && !sending && !listening) {
                      resetDraft();
                    }
                  }}
                  placeholder={
                    t("liveTrainingTableWizardVoicePlaceholder") ||
                    "e.g. Ricardo to table 1"
                  }
                  aria-label={
                    t("liveTrainingTableVoiceCommand") || "Voice command"
                  }
                />
                <LevelMeter level={level} active={listening} />
              </div>

              <CoachVoiceButton
                embedded
                disabled={micDisabled}
                languageHint={languageHint}
                onTranscript={handleTranscript}
                onPartial={handlePartial}
                onListeningChange={handleListeningChange}
                onLevelChange={setLevel}
                autoStartToken={autoStartToken}
                primaryColor={primaryColor}
                className="lt-pingflo-voice__mic"
              />

              <button
                type="button"
                className="lt-pingflo-voice__send"
                onClick={handleSendClick}
                disabled={!canSend}
                aria-label={
                  t("liveTrainingTableWizardVoiceSend") || t("send") || "Send"
                }
                title={
                  t("liveTrainingTableWizardVoiceSend") || t("send") || "Send"
                }
              >
                <SendIcon fontSize="small" />
              </button>

              <button
                type="button"
                className="lt-pingflo-voice__close"
                onClick={resetDraft}
                disabled={sending}
                aria-label={t("close")}
              >
                <CloseIcon fontSize="small" />
              </button>
            </div>
          </div>,
          portalRoot
        )
      : null;

  if (variant === "micro") {
    return (
      <div
        ref={rootRef}
        className={[
          "lt-table-wizard__voice",
          "lt-table-wizard__voice--micro",
          "lt-coach-tables-dialog__pingflo-micro",
          listening ? "is-listening" : "",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <button
          type="button"
          className={`lt-coach-tables-dialog__pingflo-mic${
            listening ? " is-listening" : ""
          }${open ? " is-open" : ""}`}
          onClick={openListener}
          disabled={micDisabled}
          aria-label={t("liveTrainingTableWizardVoiceLabel") || "Pingflo"}
          title={t("liveTrainingTableWizardVoiceLabel") || "Pingflo"}
        >
          <span className="lt-coach-tables-dialog__pingflo-mark" aria-hidden>
            <DigipingMark
              agentId="pingflo"
              size={22}
              primary={primaryColor}
              showGlow={listening || open}
              title={t("liveTrainingTableWizardVoiceLabel") || "Pingflo"}
            />
          </span>
        </button>
        {listenerCaption}
      </div>
    );
  }

  const rootClass = [
    "lt-table-wizard__voice",
    "lt-coach-tables-dialog__pingflo-voice",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={rootClass}>
      <div className="lt-table-wizard__tool-group lt-table-wizard__tool-group--voice">
        <span className="lt-table-wizard__tool-label">
          {t("liveTrainingTableWizardVoiceLabel") || "Pingflo"}
        </span>
        <div className="lt-table-wizard__voice-row">
          <button
            type="button"
            className="lt-pingflo-voice__open-card"
            onClick={openListener}
            disabled={micDisabled}
          >
            <DigipingMark
              agentId="pingflo"
              size={26}
              primary={primaryColor}
              showGlow={listening || open}
            />
            <span>
              {t("liveTrainingTableVoiceOpenListener") || "Voice command"}
            </span>
          </button>
          <p className="lt-table-wizard__voice-hint">
            {t("liveTrainingTableWizardVoiceHint") ||
              "Speak to rotate, sort, move, or switch present players."}
          </p>
        </div>
      </div>
      {listenerCaption}
    </div>
  );
}

export default LiveTrainingTableSetupPingfloVoice;
